import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";
import { NavLink } from "react-router-dom";
import { Icon } from "../icon/icon";

export const TooltipProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <TooltipPrimitive.Provider delayDuration={100}>
      {children}
    </TooltipPrimitive.Provider>
  );
};

export interface TooltipProps {
  children: React.ReactNode | string;
  content: React.ReactNode | string;
  side?: "top" | "bottom" | "left" | "right";
  align?: "center" | "start" | "end";
}

export const Tooltip = ({
  children,
  content,
  side = "top",
  align = "center",
}: TooltipProps): React.ReactNode => {
  const [open, setOpen] = React.useState(false);

  return (
    <TooltipPrimitive.Root open={open} onOpenChange={setOpen}>
      <TooltipPrimitive.Trigger
        asChild
        onClick={() => {
          setOpen(true);
        }}
        onBlur={() => {
          setOpen(false);
        }}
      >
        {typeof children === "string" ? <span>{children}</span> : children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={8}
          align={align}
          className="fui-tooltip"
        >
          {typeof content === "string" ? (
            <span className="fui-tooltip-content">{content}</span>
          ) : (
            content
          )}
          {/* <TooltipPrimitive.Arrow className="fui-tooltip-arrow" /> */}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
};

export const InfoTooltip = (props: { content: React.ReactNode | string }) => {
  const { content, ...rest } = props;
  return (
    <Tooltip content={content}>
      <div className="inline-flex text-greyscale-70">
        <Icon name="info-circle" size={16} {...rest} />
      </div>
    </Tooltip>
  );
};

export const QuestionTooltip = ({
  content,
}: {
  content: React.ReactNode | string;
}) => {
  return (
    <Tooltip content={content}>
      <div className="inline-flex text-greyscale-70">
        <Icon name="question-circle" size={16} />
      </div>
    </Tooltip>
  );
};

export function TooltipContent({
  title,
  cta,
  href,
  target,
  onClick,
}: {
  title: string;
  cta?: string;
  href?: string;
  target?: string;
  onClick?: () => void;
}) {
  return (
    <div className="flex max-w-xs flex-col items-center space-y-3 p-4 text-center">
      <p className="text-sm text-gray-700">{title}</p>
      {cta &&
        (href ? (
          <NavLink
            to={href}
            {...(target ? { target } : {})}
            className="mt-4 w-full rounded-md border border-black bg-black px-3 py-1.5 text-center text-sm text-white transition-all hover:bg-white hover:text-foreground"
          >
            {cta}
          </NavLink>
        ) : onClick ? (
          <button
            type="button"
            className="mt-4 w-full rounded-md border border-black bg-black px-3 py-1.5 text-center text-sm text-white transition-all hover:bg-white hover:text-foreground"
            onClick={onClick}
          >
            {cta}
          </button>
        ) : null)}
    </div>
  );
}

export function SimpleTooltipContent({
  title,
  cta,
  href,
}: {
  title: string;
  cta: string;
  href: string;
}) {
  return (
    <div className=" px-4 py-2 text-center text-sm text-gray-700">
      {title}{" "}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex text-gray-500 underline underline-offset-4 hover:text-gray-800"
      >
        {cta}
      </a>
    </div>
  );
}
